import { useEffect, useRef, useState } from "react";
import {
  FileText,
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import clsx from "clsx";
import { api, postForm } from "../api";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";
import { useT } from "../i18n";

interface DocOut {
  id: string;
  title: string;
  embedding_status: string;
  summary: string | null;
  summary_status: string;
  chunk_count: number;
  project_id: string | null;
  created_at: string;
}

interface ExternalSourceCatalog {
  slug: string;
  label: string;
  description: string;
  row_count: number;
}

interface ProjectOut {
  id: string;
  name: string;
  owner: string;
  custom_instructions: string | null;
  external_sources: string[];
  created_at: string;
}

export default function KnowledgePanel({ projectId }: { projectId: string }) {
  const [docs, setDocs] = useState<DocOut[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Externe Knowledge-Quellen — Katalog (statisch vom Server) + aktuelle
  // Auswahl des Projekts.
  const [catalog, setCatalog] = useState<ExternalSourceCatalog[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const toast = useToast();
  const modal = useModal();
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setDocs(
        await api.get<DocOut[]>(
          `/documents?project_id=${encodeURIComponent(projectId)}&limit=100`
        )
      );
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      setDocs((current) => {
        if (
          current.some(
            (d) =>
              d.embedding_status === "pending" ||
              d.embedding_status === "processing" ||
              d.summary_status === "pending"
          )
        ) {
          load();
        }
        return current;
      });
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Externe Quellen: Katalog 1× holen (ändert sich nicht), Projekt-Auswahl
  // pro projectId. Beide unabhängig — Katalog scheitert nicht, wenn das
  // Projekt grade neu ist.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.get<ExternalSourceCatalog[]>(
          "/projects/external-sources"
        );
        if (!cancelled) setCatalog(c);
      } catch {
        /* still allow file upload to work */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.get<ProjectOut>(`/projects/${projectId}`);
        if (!cancelled) setSelected(Array.isArray(p.external_sources) ? p.external_sources : []);
      } catch {
        if (!cancelled) setSelected([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const toggleExternal = async (slug: string) => {
    const isOn = selected.includes(slug);
    const next = isOn ? selected.filter((s) => s !== slug) : [...selected, slug];
    setSavingSlug(slug);
    setSelected(next);  // optimistic
    try {
      const updated = await api.patch<ProjectOut>(`/projects/${projectId}`, {
        external_sources: next,
      });
      // Server-Antwort gewinnt — Backend filtert unbekannte Slugs.
      setSelected(Array.isArray(updated.external_sources) ? updated.external_sources : next);
    } catch (e) {
      setSelected(selected);  // rollback
      toast.err(t.common.speichernFehlgeschlagen, (e as Error).message);
    } finally {
      setSavingSlug(null);
    }
  };

  const onFile = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      await postForm<DocOut>("/documents", fd);
      toast.ok(t.shared.wissen.hochgeladen, file.name);
      await load();
    } catch (e) {
      toast.err(t.shared.wissen.uploadFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
      setOver(false);
    }
  };

  const delDoc = async (id: string, title: string) => {
    const ok = await modal.confirm({
      title: t.shared.wissen.loeschenFrage(title),
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocs((s) => s.filter((d) => d.id !== id));
    } catch (e) {
      toast.err(t.shared.wissen.loeschenFehlgeschlagen, (e as Error).message);
    }
  };

  // Counter: "Knowledge (N)" wenn nur Files, "Knowledge (N + M)" wenn beides.
  const counterLabel = (() => {
    const n = docs.length;
    const m = selected.length;
    if (n === 0 && m === 0) return null;
    if (m === 0) return `(${n})`;
    if (n === 0) return `(+${m})`;
    return `(${n} + ${m})`;
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
          {t.shared.wissen.titel}{" "}
          {counterLabel && (
            <span className="text-text-3 font-normal">{counterLabel}</span>
          )}
        </h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="text-[11px] inline-flex items-center gap-1 text-text-2 hover:text-brand-600"
          title={t.shared.wissen.dateiHinzufuegen}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {t.shared.wissen.dateiKnopf}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.md,.markdown"
          aria-label={t.shared.wissen.dateiFeldAria}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            if (e.target) e.target.value = "";
          }}
          className="sr-only"
        />
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
        className={clsx(
          "rounded-md border-2 border-dashed transition-colors",
          over
            ? "border-brand-600 bg-brand-50/50 dark:bg-brand-700/10 p-3"
            : "border-transparent p-0"
        )}
      >
        {docs.length === 0 ? (
          <div className="text-[11px] text-text-3 italic px-2 py-4 text-center border border-dashed border-border rounded-md">
            {t.shared.wissen.ablage}
          </div>
        ) : (
          <ul className="space-y-1">
            {docs.map((d) => (
              <li
                key={d.id}
                className="group bg-surface-2 rounded-md px-2 py-1.5"
              >
                <div className="flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-text-3 shrink-0 mt-0.5" />
                  <button
                    onClick={() =>
                      setExpanded((cur) => (cur === d.id ? null : d.id))
                    }
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-[13px] font-medium truncate flex items-center gap-1">
                      <span className="truncate">{d.title}</span>
                      {d.embedding_status === "error" && (
                        <AlertCircle className="w-3 h-3 text-danger shrink-0" />
                      )}
                      {(d.embedding_status === "pending" ||
                        d.embedding_status === "processing") && (
                        <Loader2 className="w-3 h-3 text-warning animate-spin shrink-0" />
                      )}
                    </div>
                    {expanded === d.id && d.summary && (
                      <div className="text-[11px] text-text-2 mt-1 leading-snug">
                        {d.summary}
                      </div>
                    )}
                    {expanded === d.id && !d.summary && d.summary_status === "pending" && (
                      <div className="text-[11px] text-text-3 italic mt-1">
                        {t.shared.wissen.summaryLaeuft}
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => delDoc(d.id, d.title)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-text-3 hover:text-danger transition-opacity"
                    title={t.common.loeschen}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Externe Knowledge-Quellen (Supabase-Vektoren). Bewusst hier UNTER
          den Files — visuell als selbständiger Bereich, aber im selben
          "Knowledge"-Header gezählt. */}
      {catalog.length > 0 && (
        <div className="mt-3 pt-2 border-t border-border/70">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-text-3 mb-1.5">
            {t.shared.wissen.externeQuellen}
          </div>
          <ul className="space-y-1">
            {catalog.map((s) => {
              const checked = selected.includes(s.slug);
              const saving = savingSlug === s.slug;
              return (
                <li
                  key={s.slug}
                  className="bg-surface-2 rounded-md px-2 py-1.5"
                >
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={() => toggleExternal(s.slug)}
                      className="mt-0.5 accent-brand-600 cursor-pointer"
                    />
                    <Sparkles className="w-3.5 h-3.5 text-text-3 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate flex items-center gap-1">
                        {s.label}
                        {saving && (
                          <Loader2 className="w-3 h-3 text-warning animate-spin shrink-0" />
                        )}
                      </div>
                      <div className="text-[11px] text-text-2 leading-snug">
                        {s.description}
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
